import { Component } from 'react';
import { debounce, get } from 'lodash';
import { PER_PAGE_OPTION_1, PER_PAGE_OPTION_2 } from '../../constants/admin/tables';

/**
 * Base class containing shared logic for AdminTable and EditableAdminTable
 * This allows code reuse through inheritance without duplicating methods
 */
class BaseAdminTable extends Component {
    componentDidMount() {
        this.props.initTable(this.props.tableIndex || 0, this.props.model);
        this._initTable();
    }

    componentDidUpdate() {
        this._initTable();
    }

    _allowRowInteraction = item => !this.props.blockRowInteraction || !this.props.blockRowInteraction(item);

    _allowRowSelection = item => !this.props.blockRowSelection || !this.props.blockRowSelection(item);

    _initTable = () => {
        if (this.props.fetchDataFunction && !this.props.fetching && !this.props.fetched) {
            this._fetch(
                get(this.props, `preferences.tables.${this.props.model}.page`) || 1,
                get(this.props, `preferences.tables.${this.props.model}.resultsPerPage`) ||
                    this.props.forceInitialPages ||
                    (this.props.subContextId ? PER_PAGE_OPTION_1 : PER_PAGE_OPTION_2)
            );
        }
    };

    _updateFilter = (value, name) => {
        this.props.updateFilter(this.props.tableIndex || 0, name, value);
        this.props.search(this.props.tableIndex, this.props.searchQuery, this.props.model);
    };

    _fetch = (page, resultsPerPage) => {
        this.props.fetchDataFunction(
            this.props.subContextId || this.props.contextId,
            {
                prefKey: this.props.model,
                sort: this.props.sort ? `${this.props.sortDirection}${this.props.sort}` : '',
                query: this.props.searchQuery,
                page,
                resultsPerPage
            },
            this.props.contextId,
            this.props.type
        );
    };

    _select = (id, item) => {
        if (this._allowRowInteraction(item) && this._allowRowSelection(item)) {
            this.props.select(id, this.props.tableIndex, item);
        }
    };

    _isSelected = item => this.props.selection.includes(item.id);

    _selectAll = () => {
        const tableData = this.props.filterFunction
            ? this.props.filterFunction(this.props.data || this.props.tableData)
            : this.props.data || this.props.tableData;

        const allCurrentPageSelected = tableData.length > 0 && tableData.every(el => this._isSelected(el));
        const hasAnySelection = this.props.selection.length > 0;

        if (allCurrentPageSelected || hasAnySelection) {
            const selectionItems = this.props.selectionItems || [];
            selectionItems.forEach(item => {
                this._select(item.id, item);
            });
        } else {
            tableData.forEach(el => this._select(el.id, el));
        }
    };

    _selectAllChecked = () => {
        const tableData = this.props.filterFunction
            ? this.props.filterFunction(this.props.data || this.props.tableData)
            : this.props.data || this.props.tableData;
        const allCurrentPageSelected = tableData.length > 0 && tableData.every(el => this._isSelected(el));
        const hasAnySelection = this.props.selection.length > 0;
        const currentPageSelectedIds = tableData.filter(el => this._isSelected(el)).map(el => el.id);
        const hasSelectionFromOtherPages =
            hasAnySelection && this.props.selection.some(id => !currentPageSelectedIds.includes(id));

        return {
            checked: allCurrentPageSelected && !hasSelectionFromOtherPages,
            halfChecked: hasAnySelection && (!allCurrentPageSelected || hasSelectionFromOtherPages)
        };
    };

    _open = item => {
        if (this._allowRowInteraction(item)) {
            this.props.openItem
                ? this.props.openItem(item)
                : this.props.openAction(item.id, this.props.model, this.props.subContextId);
        }
    };

    _buttonAction = () => {
        return this.props.buttonAction
            ? this.props.buttonAction()
            : this.props.newAction(this.props.model, this.props.subContextId, this.props.type);
    };

    _changeSort = newSort => this.props.changeSort(this.props.tableIndex, newSort, this.props.model);

    _search = debounce(this.props.search, 350);

    _debouncedSearch = e => {
        this._search(this.props.tableIndex, e.target.value, this.props.model);
    };
}

export default BaseAdminTable;
